import { motion } from "framer-motion";
import { Phone, Star, Flag } from "lucide-react";
import { PHONE, PHONE_HREF } from "./site";
import { EstimateDialog } from "./EstimateDialog";

export default function CtaFooter() {
  return (
    <>
      <section data-testid="cta-section" className="relative bg-[#0F172A] py-20 lg:py-28 overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-[0.06] [background:radial-gradient(#FDFBF7_1px,transparent_1px)] [background-size:26px_26px]" />
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center justify-center gap-2 mb-8">
              <Star size={13} className="text-[#D97706] fill-[#D97706]" />
              <span className="font-jet text-[11px] uppercase tracking-[0.3em] text-[#D96B43]">Free Estimates · Today</span>
              <Star size={13} className="text-[#D97706] fill-[#D97706]" />
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#FDFBF7]">
              Ready to clear your <em className="italic text-[#D96B43]">honey-do list?</em>
            </h2>
            <p className="mt-6 text-[#94A3B8] text-base sm:text-lg max-w-xl mx-auto">
              One call handles the cleaning and the fixing. Call today — free same-day estimates across Port St. Lucie &amp; the Treasure Coast.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <a
                data-testid="cta-call-button"
                href={PHONE_HREF}
                className="group flex items-center gap-3 bg-[#D96B43] text-white pl-6 pr-7 py-4 rounded-full font-jet text-sm tracking-wider hover:bg-[#FDFBF7] hover:text-[#0F172A] transition-colors duration-300"
              >
                <Phone size={16} className="group-hover:rotate-12 transition-transform duration-300" />
                {PHONE}
              </a>
              <EstimateDialog
                testId="cta-estimate-button"
                label="Request Estimate Online"
                className="px-7 py-4 rounded-full border border-[#FDFBF7]/30 font-jet text-sm tracking-wider text-[#FDFBF7] hover:border-[#D96B43] hover:text-[#D96B43] transition-colors duration-300"
              />
            </div>
          </motion.div>
        </div>
      </section>

      <footer data-testid="site-footer" className="bg-[#0A0F1E] pt-16 pb-10">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-10 pb-12 border-b border-[#FDFBF7]/10">
          <div>
            <div className="flex items-center gap-3">
              <img
                src="/images/logo-round.png"
                alt="His and Hers Cleaning Services and Handyman logo"
                data-testid="footer-logo"
                className="w-16 h-16 rounded-full object-cover ring-2 ring-[#FDFBF7]/15"
              />
              <span className="font-display text-xl text-[#FDFBF7]">
                His &amp; Hers <span className="text-[#D96B43] italic">Cleaning + Handyman</span>
              </span>
            </div>
            <p className="mt-4 text-sm text-[#64748B] leading-relaxed">
              Proud Saint Lucie residents for 40 years. One local family team for cleaning + handyman across the Treasure Coast.
            </p>
          </div>
          <div>
            <span className="font-jet text-[10px] uppercase tracking-[0.25em] text-[#D96B43]">Service Areas</span>
            <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-[#94A3B8]" data-testid="footer-service-areas">
              {["Port St. Lucie", "St. Lucie West", "Tradition", "Fort Pierce", "Jensen Beach", "Stuart", "Palm City", "Hutchinson Island"].map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          </div>
          <div>
            <span className="font-jet text-[10px] uppercase tracking-[0.25em] text-[#D96B43]">Contact</span>
            <ul className="mt-4 space-y-2 text-sm text-[#94A3B8]">
              <li>
                <a data-testid="footer-phone-link" href={PHONE_HREF} className="text-[#FDFBF7] hover:text-[#D96B43] transition-colors duration-300">
                  {PHONE}
                </a>
              </li>
              <li>Treasure Coast, FL</li>
              <li>Mon–Sat 7am–7pm</li>
              <li>Same-day estimates</li>
            </ul>
          </div>
          <div>
            <span className="font-jet text-[10px] uppercase tracking-[0.25em] text-[#D96B43]">Trust</span>
            <ul className="mt-4 space-y-2 text-sm text-[#94A3B8]">
              <li>Licensed &amp; Insured</li>
              <li>LLC #L26000381246</li>
              <li>Locally Owned &amp; Operated</li>
              <li className="flex items-center gap-2 text-[#FDFBF7]">
                <Flag size={14} className="text-[#D96B43]" />
                Proudly American-Owned
              </li>
            </ul>
          </div>
        </div>
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-jet text-[10px] uppercase tracking-[0.2em] text-[#475569]">
            © 2026 His and Hers Cleaning Services and Handyman LLC
          </p>
          <p className="font-jet text-[10px] uppercase tracking-[0.2em] text-[#475569]">All rights reserved</p>
          <p data-testid="footer-credit" className="font-jet text-[10px] uppercase tracking-[0.2em] text-[#475569]">
            Website by{" "}
            <a
              href="https://antcrawler.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#D96B43] hover:text-[#FDFBF7] transition-colors duration-300"
            >
              Antcrawler
            </a>
          </p>
        </div>
      </footer>
    </>
  );
}
